import React from 'react';
import { useScrollAnimation } from './hooks/ScrollAnimation';
import { experiencesData } from '../data/experiencesData';
import { useTheme } from '../context/ThemeContext';

const Experience = () => {
  const { isDark } = useTheme();
  const experiences = experiencesData;

  const sortedExperiences = [...experiences].sort((a, b) => b.id - a.id);

  const { ref: titleRef, isVisible: titleVisible } = useScrollAnimation({ threshold: 0.1 });

  // posisi 3 → pixel-grid (transparan, bergantian dengan RuleOfLife solid di posisi 2)
  const titleColor = isDark ? 'text-white' : 'text-gray-800';
  const subtitleColor = isDark ? 'text-slate-400' : 'text-gray-600';

  return (
    <section id="experience" className="py-8 md:py-12 px-4 pixel-grid">
      <div className="container mx-auto max-w-5xl">

        {/* Judul */}
        <div
          ref={titleRef}
          style={{
            opacity: titleVisible ? 1 : 0,
            transform: titleVisible ? 'translateY(0)' : 'translateY(-24px)',
            transition: 'opacity 0.6s ease-out, transform 0.6s ease-out',
          }}
        >
          <h2 className={`text-xl md:text-2xl lg:text-3xl font-bold text-center ${titleColor} mb-2 pixel-font`}>
            <span className="glitch-text" data-text="PENGALAMAN">PENGALAMAN</span>
          </h2>
          <p className={`text-center ${subtitleColor} mb-8 md:mb-12 text-sm md:text-base`}>
            Perjalanan saya di dunia pemrograman ({experiences.length})
          </p>
        </div>

        {/* Cards dengan stagger */}
        <div className="space-y-6 md:space-y-8">
          {sortedExperiences.map((exp, index) => (
            <ExperienceCard key={exp.id} exp={exp} index={index} isDark={isDark} />
          ))}
        </div>
      </div>
    </section>
  );
};

// Sub-komponen terpisah agar setiap kartu punya IntersectionObserver sendiri
const ExperienceCard = ({ exp, index, isDark }) => {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.1 });
  const delay = index * 100; // stagger 100ms per kartu

  const cardBg = isDark ? 'bg-[#0D1520]' : 'bg-white';
  const titleColor = isDark ? 'text-slate-100' : 'text-gray-800';
  const companyColor = isDark ? 'text-blue-400' : 'text-blue-600';
  const periodBg = isDark ? 'bg-[#111C2C] text-slate-400' : 'bg-gray-100 text-gray-500';
  const descColor = isDark ? 'text-slate-300' : 'text-gray-700';
  const techBg = isDark ? 'bg-[#111C2C] text-slate-300' : 'bg-gray-100 text-gray-800';

  return (
    <div
      ref={ref}
      className={`pixel-border ${cardBg} p-4 md:p-6 relative cursor-pointer hover:scale-[1.02] hover:shadow-[0px_0px_12px_rgba(59,130,246,0.3)]`}
      style={{
        transitionDelay: `${delay}ms`,
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateX(0)' : 'translateX(-40px)',
        transition: `opacity 0.6s ease-out ${delay}ms, transform 0.6s ease-out ${delay}ms, box-shadow 0.3s, scale 0.3s`,
      }}
    >
      <div className="absolute -left-1 md:-left-2 top-6 w-3 h-3 md:w-4 md:h-4 bg-blue-600 transform rotate-45" />

      <div className="flex flex-col md:flex-row md:justify-between md:items-start mb-3 md:mb-4">
        <div className="mb-2 md:mb-0">
          <h3 className={`text-lg md:text-xl font-bold ${titleColor}`}>{exp.title}</h3>
          <p className={`${companyColor} font-semibold text-sm md:text-base`}>{exp.company}</p>
        </div>
        <p className={`${periodBg} px-2 py-1 pixel-border text-xs md:text-sm self-start md:self-auto`}>
          {exp.period}
        </p>
      </div>

      <p className={`${descColor} mb-3 md:mb-4 text-sm md:text-base leading-relaxed`}>
        {exp.description}
      </p>

      <div className="flex flex-wrap gap-1 md:gap-2">
        {exp.technologies.map((tech, i) => (
          <span
            key={i}
            className={`px-2 py-1 ${techBg} text-[10px] md:text-xs pixel-border font-medium hover:bg-blue-600 hover:text-white transition-colors duration-200`}
          >
            {tech}
          </span>
        ))}
      </div>
    </div>
  );
};

export default Experience;
